const allowedChars =
	'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=._-'
const charPoolSize = 2048
const charChangeMinMs = 900
const charChangeJitterMs = 900
const baseCellSize = 18
const lightCellScale = 1.6
const hoverRadius = 60
const shrinkSpeedThreshold = 2.5
const shrinkFactor = 0.65
const decay = 0.97
const minIntensity = 0.02
const frameInterval = 1000 / 24 // target ~24fps, skip frames if faster
const densityScale = 1.5 // enlarge cells to reduce cols/rows
const resolutionScale = 0.7 // render canvas at 70% res, upscale via transform
const ease = 0.18
const idleDelayMs = 2200
const fadeLerpIn = 0.25
const fadeLerpOut = 0.08

export function createState(canvas) {
	const state = {
		canvas,
		ctx: canvas.getContext('2d'),
		font: readFontSettings(canvas),
		dimensions: {
			width: 0,
			height: 0,
			cols: 0,
			rows: 0,
			cellSize: baseCellSize,
		},
		grid: [],
		fade: 1,
		pointer: {
			x: -1000,
			y: -1000,
		},
		light: {
			x: -1000,
			y: -1000,
			prevX: -1000,
			prevY: -1000,
			prevVx: 0,
			prevVy: 0,
		},
		timing: {
			lastTime: 0,
			startTime: null,
			lastPointerTs: performance.now(),
		},
		palette: readPalette(canvas),
		ellipse: {
			major: hoverRadius,
			minor: hoverRadius,
			angle: 0,
			isCircle: true,
		},
		charPool: buildCharPool(),
		charPoolIndex: 0,
	}
	return state
}

export function resizeCanvas(state) {
	const devicePixelRatioValue = window.devicePixelRatio || 1
	const scaledRatio = devicePixelRatioValue * resolutionScale
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	state.dimensions.width = viewportWidth
	state.dimensions.height = viewportHeight

	state.canvas.width = viewportWidth * scaledRatio
	state.canvas.height = viewportHeight * scaledRatio
	state.canvas.style.width = `${viewportWidth}px`
	state.canvas.style.height = `${viewportHeight}px`
	state.canvas.style.backgroundColor = state.palette.bg
	state.font = readFontSettings(state.canvas)

	const transformMatrix = {
		scaleX: scaledRatio,
		skewX: 0,
		skewY: 0,
		scaleY: scaledRatio,
		translateX: 0,
		translateY: 0,
	}
	const { scaleX, skewX, skewY, scaleY, translateX, translateY } =
		transformMatrix
	state.ctx.setTransform(scaleX, skewX, skewY, scaleY, translateX, translateY)
	state.ctx.imageSmoothingEnabled = false

	state.cellSize = getCellSize(state) * densityScale
	state.dimensions.cellSize = state.cellSize
	state.dimensions.cols =
		Math.ceil(state.dimensions.width / state.cellSize) + 2
	state.dimensions.rows =
		Math.ceil(state.dimensions.height / state.cellSize) + 2

	state.grid = buildGrid(state)
}

function getCellSize(state) {
	const computedSize = isLightTheme(state)
		? Math.round(baseCellSize * lightCellScale)
		: baseCellSize
	return computedSize
}

export function buildGrid(state) {
	const rowCount = state.dimensions.rows
	const columnCount = state.dimensions.cols
	const grid = []
	for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
		const row = createRow(columnCount, state)
		grid.push(row)
	}
	return grid
}

function createRow(columnCount, state) {
	const row = []
	for (let columnIndex = 0; columnIndex < columnCount; columnIndex++) {
		const cell = createCell(state)
		row.push(cell)
	}
	return row
}

function createCell(state) {
	const charValue = randomChar(state)
	const intensityValue = getBiasedIntensity(state)
	const now = performance.now()
	const nextChange =
		now + charChangeMinMs + Math.random() * charChangeJitterMs
	const cell = { char: charValue, intensity: intensityValue, nextChange }
	return cell
}

function updateGrid(state, timestamp) {
	const columnCount = state.dimensions.cols
	const rowCount = state.dimensions.rows
	for (let y = 0; y < rowCount; y++) {
		for (let x = 0; x < columnCount; x++) {
			const cell = state.grid[y][x]
			if (timestamp >= cell.nextChange) {
				cell.char = randomChar(state)
				cell.intensity = getBiasedIntensity(state)
				cell.nextChange =
					timestamp +
					charChangeMinMs +
					Math.random() * charChangeJitterMs
			}
			const decayedIntensity = cell.intensity * decay
			cell.intensity = Math.max(decayedIntensity, minIntensity)
		}
	}
}

function drawGrid(state) {
	const { ctx, grid, palette, dimensions } = state
	const rows = dimensions.rows
	const cols = dimensions.cols
	const cellSize = state.cellSize
	ctx.clearRect(0, 0, dimensions.width, dimensions.height)
	ctx.textBaseline = 'top'
	ctx.textAlign = 'center'
	ctx.font = state.font

	for (let y = 0; y < rows; y++) {
		for (let x = 0; x < cols; x++) {
			const cell = grid[y][x]
			const positionX = x * cellSize + cellSize / 2
			const positionY = y * cellSize + 2
			const boostedIntensity = getBoostedIntensity(
				state,
				cell.intensity,
				{
					x: positionX,
					y: positionY,
				}
			)
			const colorValue = computeColorValue(palette, boostedIntensity)
			const alphaValue = computeAlpha(boostedIntensity, state.fade)
			if (alphaValue < 0.02) continue

			ctx.fillStyle = `rgba(${colorValue}, ${colorValue}, ${colorValue}, ${alphaValue})`
			ctx.fillText(cell.char, positionX, positionY)
		}
	}
}

export function startLoop(state) {
	function animationStep(timestamp) {
		if (!state.timing.startTime) state.timing.startTime = timestamp
		if (!state.timing.lastTime) state.timing.lastTime = timestamp
		const delta = timestamp - state.timing.lastTime

		if (delta >= frameInterval) {
			updateLightPosition(state)
			updateFade(state, timestamp)
			updateGrid(state, timestamp)
			drawGrid(state)
			state.timing.lastTime = timestamp
		}

		requestAnimationFrame(animationStep)
	}

	requestAnimationFrame(animationStep)
}

function updateLightPosition(state) {
	state.light.prevX = state.light.x
	state.light.prevY = state.light.y
	state.light.x += (state.pointer.x - state.light.x) * ease
	state.light.y += (state.pointer.y - state.light.y) * ease
	updateEllipse(state)
}

function handlePointerMove(state, event) {
	const rect = state.canvas.getBoundingClientRect()
	state.pointer.x = event.clientX - rect.left
	state.pointer.y = event.clientY - rect.top
	state.timing.lastPointerTs = performance.now()
}

function handlePointerLeave(state) {
	state.pointer.x = -1000
	state.pointer.y = -1000
	state.light.x = -1000
	state.light.y = -1000
	state.light.prevX = -1000
	state.light.prevY = -1000
	state.light.prevVx = 0
	state.light.prevVy = 0
	state.timing.lastPointerTs = performance.now()
}

function isLightTheme(state) {
	const isLight = state.palette.brightnessBias >= 0.5
	return isLight
}

function getBiasedIntensity(state) {
	const bias = state.palette.brightnessBias ?? 0.5

	if (bias >= 0.4) {
		const isRareDark = Math.random() < 0.05
		const biasedIntensity = isRareDark
			? 0.15 + Math.random() * 0.2
			: 0.75 + Math.random() * 0.25
		return biasedIntensity
	}

	const exponent = Math.max(0.05, 1 - bias)
	const randomValue = Math.random() ** exponent
	const biasedIntensity = 0.2 + randomValue * 1.0
	return biasedIntensity
}

function updateEllipse(state) {
	const vx = state.light.x - state.light.prevX
	const vy = state.light.y - state.light.prevY
	const speed = Math.hypot(vx, vy)
	state.light.prevVx = vx
	state.light.prevVy = vy

	if (!Number.isFinite(speed) || speed < 0.5) {
		setEllipseToCircle(state, hoverRadius)
		return
	}

	if (speed < shrinkSpeedThreshold) {
		setEllipseToCircle(state, hoverRadius * shrinkFactor)
		return
	}

	const accel = Math.hypot(vx - state.light.prevVx, vy - state.light.prevVy)
	const majorScale = 1 + Math.min(accel * 0.25, 1.4)
	const major = hoverRadius * majorScale
	const minor = hoverRadius * 0.8
	state.ellipse.isCircle = false
	state.ellipse.angle = Math.atan2(vy, vx)
	state.ellipse.major = major
	state.ellipse.minor = minor
}

function computeBoost(state, px, py) {
	if (state.pointer.x < 0 || state.pointer.y < 0) {
		const zeroBoost = 0
		return zeroBoost
	}

	const dx = px - state.light.x
	const dy = py - state.light.y

	if (state.ellipse.isCircle) {
		const distance = Math.hypot(dx, dy)
		if (distance >= hoverRadius) {
			const zeroBoost = 0
			return zeroBoost
		}
		const circleBoost = 1 - distance / hoverRadius
		return circleBoost
	}

	const cosA = Math.cos(state.ellipse.angle)
	const sinA = Math.sin(state.ellipse.angle)

	const rx = dx * cosA + dy * sinA
	const ry = -dx * sinA + dy * cosA

	const norm =
		(rx * rx) / (state.ellipse.major * state.ellipse.major) +
		(ry * ry) / (state.ellipse.minor * state.ellipse.minor)

	if (norm >= 1) {
		const zeroBoost = 0
		return zeroBoost
	}
	const ellipseBoost = 1 - Math.sqrt(norm)
	return ellipseBoost
}

function readPalette(canvas) {
	const styles = getComputedStyle(canvas)
	const baseValue =
		Number(styles.getPropertyValue('--canvas-char-base')) || 10
	const maxValue = Number(styles.getPropertyValue('--canvas-char-max')) || 140
	const background = styles.getPropertyValue('--canvas-bg')?.trim() || '#000'
	const brightnessBias =
		Number(styles.getPropertyValue('--canvas-bright-bias')) || 0.5
	const palette = {
		base: baseValue,
		max: maxValue,
		bg: background,
		brightnessBias,
	}
	return palette
}

function readFontSettings(canvas) {
	const styles = getComputedStyle(canvas)
	const fontFamily =
		styles.getPropertyValue('--font-code-family')?.trim() ||
		'"SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace'
	const fontSize =
		styles.getPropertyValue('--font-code-size')?.trim() || '14px'
	return buildFontString(fontSize, fontFamily)
}

function buildFontString(fontSize, fontFamily) {
	const fontString = `${fontSize} ${fontFamily}`
	return fontString
}

export function observeThemeChanges(state) {
	function handleThemeChange() {
		state.palette = readPalette(state.canvas)
		state.font = readFontSettings(state.canvas)
		resizeCanvas(state)
	}

	const observer = new MutationObserver(handleThemeChange)
	observer.observe(document.documentElement, {
		attributes: true,
		attributeFilter: ['data-theme'],
	})
}

export function attachListeners(state) {
	function handleResize() {
		resizeCanvas(state)
	}

	function handleMove(event) {
		handlePointerMove(state, event)
	}

	function handleLeave() {
		handlePointerLeave(state)
	}

	window.addEventListener('resize', handleResize)
	window.addEventListener('mousemove', handleMove)
	window.addEventListener('mouseleave', handleLeave)
}

function randomChar(state) {
	state.charPoolIndex = (state.charPoolIndex + 1) % state.charPool.length
	return state.charPool[state.charPoolIndex]
}

function buildCharPool() {
	const pool = new Array(charPoolSize)
	for (let i = 0; i < charPoolSize; i++) {
		const index = Math.floor(Math.random() * allowedChars.length)
		pool[i] = allowedChars[index]
	}
	return pool
}

function updateFade(state, timestamp) {
	const idleTime = timestamp - state.timing.lastPointerTs
	const targetFade = idleTime > idleDelayMs ? 0 : 1
	const lerpFactor = targetFade > state.fade ? fadeLerpIn : fadeLerpOut
	state.fade += (targetFade - state.fade) * lerpFactor
	state.fade = clampValue(state.fade, 0, 1)
}

function getBoostedIntensity(state, baseIntensity, position) {
	const boost = computeBoost(state, position.x, position.y)
	const boostAmount = isLightTheme(state) ? -boost * 1.1 : boost * 1.2
	const boosted = baseIntensity + boostAmount
	const clamped = isLightTheme(state)
		? Math.max(boosted, 0.1)
		: Math.min(boosted, 1.8)
	return clamped
}

function computeColorValue(palette, intensity) {
	const colorValue = palette.base + intensity * (palette.max - palette.base)
	return clampValue(colorValue, 0, 255)
}

function computeAlpha(intensity, fade) {
	const alpha = Math.min(0.4, 0.08 + intensity * 0.18) * fade
	return alpha
}

function clampValue(value, min, max) {
	return Math.min(Math.max(value, min), max)
}

function setEllipseToCircle(state, radius) {
	state.ellipse.major = radius
	state.ellipse.minor = radius
	state.ellipse.angle = 0
	state.ellipse.isCircle = true
}
